import { PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Themes } from '@/components/microstory/theme';
import { StripPlot } from '@/components/strip-plot';
import { StoryMap } from '@/components/world-map/story';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

type OnlineRisksChapter2Props = {
  data: OnlineRisksData['concerned_with_sharing_personal_info']['very_concerned_breakdown'];
};

export function OnlineRisksChapter2(props: OnlineRisksChapter2Props) {
  const { data } = props;

  const formatPercentage = useNumberFormatting('percent');

  const [highlightedIds, setHighlightedIds] = useState<string[]>();

  const handleStepEnter = (evt: OnStepEnterExitArgs<string[] | undefined>) => {
    setHighlightedIds(evt.data);
  };

  const stripPlotData = (Object.entries(data) as [CountryIsoCode, number][]).map(
    ([countryCode, value]) => ({
      id: countryCode,
      label: countryIdToName(countryCode),
      value,
    })
  );

  return (
    <>
      <ScrollRoot>
        <ScrollGraphic>
          <StoryMap theme={Themes.fire} highlightedCountries={['NG', 'UK']} />
        </ScrollGraphic>
        <ScrollSteps>
          <ScrollStep>
            <StoryText>
              <Trans id="online-risks.chapter-2.text-1">
                Views vary greatly across{' '}
                <PrimaryAnnotatedType>countries&hellip;</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StripPlot
            highlightedIds={highlightedIds}
            data={stripPlotData}
            formatValue={formatPercentage}
            axisLabel={t({
              id: 'online-risks.strip-plot.label',
              message:
                '% of young internet users who are very concerned about personal information being shared',
            })}
            getValue={(d) => d.value}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStepEnter}>
          <ScrollStep data={['NG']}>
            <StoryText>
              <Trans id="online-risks.chapter-2.text-2">
                In <PrimaryAnnotatedType>{countryIdToName('NG')}, </PrimaryAnnotatedType>{' '}
                {/* 
                  This story makes use of an initial limited set of country codes which has been extended as type
                  To make this extended union compatible with the limited set, we make use of the Partial util which makes the country codes optional
                */}
                {data.NG && formatPercentage(data.NG)} of young people say they are "very
                concerned".
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['UK']}>
            <StoryText>
              <Trans id="online-risks.chapter-2.text-3">
                {/* See comment above */}
                Only {data.UK && formatPercentage(data.UK)} of young people gave the same answer in
                the <PrimaryAnnotatedType>{countryIdToName('UK')}.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
